import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { get, groupBy, map } from 'lodash';
import { Divider, LinearProgress, Paper, Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from 'material-ui';
import pallet from '../../constants/colors';
import Pagination from '../../shared/Pagination';
import { getSettingByKey, setSettingByKey } from '../../utils/localSettings';

const ITEMS_PER_PAGE_LOCAL_KEY = 'inOutItemsPerPage';

const columnStyle = {
  whiteSpace: 'normal',
  paddingRight: 0,
  paddingLeft: 0,
  border: '1px groove',
};

const InOutTable = ({ entries, table }) => {
  const HALF_DAY_HOURS = 6;
  const [placedDiv, setPlacedDiv] = useState({
    top: '-9999px',
    left: '-9999px',
    padding: 12,
    background: 'white',
    position: 'absolute',
    textAlign: 'center',
    boxShadow: '5px 10px 18px #888888',
  });

  const [inOutHoverTexts, setInOutHoverTexts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getSettingByKey(ITEMS_PER_PAGE_LOCAL_KEY) || 10);
  const [dataSource, setDataSource] = useState([]);

  const { loading, clockTypesById } = useSelector(state => state.inOutReducer);

  useEffect(() => {
    const data = map(entries, rec => ({ ...rec, ClockCardEntries: groupBy(rec.ClockCardEntries, t => moment(t.ClockIn).format('DD/MM/YY')) }));
    setDataSource(data);
    data.length > 0 ? setCurrentPage(1) : setCurrentPage(0);
  }, [entries]);

  const getTypeColor = (clock) => {
    if (clock.Active && clock.ClockCardEntryTypeName) {
      return get(clockTypesById, [clock.ClockCardEntryTypeName, 'color']);
    }
    return pallet.errorColor;
  };
  const getCell = (clockarr) => {
    const moreThanOneEntry = clockarr.length > 1;
    const clock = clockarr[0];
    const start = moment(clock.ClockIn);
    const end = moment(clock.ClockOut);
    const duration = moment.duration(end.diff(start));
    const hours = duration.asHours();
    const styleBase = {
      color: 'white',
      border: 'none',
      borderRadius: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      boxShadow: 'none',
      justifyContent: 'center',
    };
    if (moreThanOneEntry) {
      return (
        <span
          key={get(clock, 'ClockCardEntryID')}
          style={{ ...styleBase, background: pallet.warningColor, alignItems: 'center' }}
        >
          *
        </span>
      );
    }
    return (
      <span
        key={get(clock, 'ClockCardEntryID')}
        style={{
          ...styleBase,
          ...(hours <= HALF_DAY_HOURS && { backgroundImage: `linear-gradient(45deg, ${getTypeColor(clock)} 50%, rgba(255, 255, 255, 0) 50%)` }),
          ...(hours > HALF_DAY_HOURS && { background: getTypeColor(clock) }),
          alignItems: hours > HALF_DAY_HOURS ? 'center' : 'end',
        }}
      >
        {get(clockTypesById, [clock.ClockCardEntryTypeName, 'value_short']) || 'FV'}
      </span>
    );
  };
  const onCellHover = (rowNumber, colNumber, event) => {
    const row = (itemsPerPage * (currentPage - 1)) + rowNumber;
    const { ClockCardEntries } = dataSource[row];
    const cells = get(ClockCardEntries, get(table.columns[colNumber - 1], 'dataIndex'));
    if (cells) {
      setPlacedDiv({
        ...placedDiv,
        top: event.pageY,
        left: event.pageX + 10
      });
      const hoverTexts = map(cells, cell => ({
        inOutType: get(clockTypesById, [cell.ClockCardEntryTypeName, 'value']) || <FormattedMessage id="inOut.type.absence" />,
        inOutClock: `${moment(cell.ClockIn).format('HH:mm')} - ${moment(cell.ClockOut).format('HH:mm')}`,
        comment: cell.ClockCardEntryComment,
      }));
      setInOutHoverTexts(hoverTexts);
    } else {
      setPlacedDiv({
        ...placedDiv,
        top: -9999,
        left: -9999
      });
      setInOutHoverTexts([]);
    }
  };
  return (
    <Paper style={{ maxWidth: 1400, margin: 'auto' }}>
      {loading && <LinearProgress mode="indeterminate" />}
      <Pagination
        data={dataSource}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={(val) => {
          setSettingByKey(ITEMS_PER_PAGE_LOCAL_KEY, val);
          setItemsPerPage(val);
        }}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        settingsKey={ITEMS_PER_PAGE_LOCAL_KEY}
      >
        {currentData => (
          <div>
            <Table
              // bodyStyle={{ overflowX: 'none', overflowY: 'none', height: 520 }}
              bodyStyle={{ overflowX: 'none', overflowY: 'none', minHeight: 520 }}
              fixedHeader
              onCellHover={onCellHover}
              onRowHover={() => null}
            >
              <TableHeader
                displaySelectAll={false}
                adjustForCheckbox={false}
                enableSelectAll={false}
              >
                <TableRow key="table-key" hoverable>
                  {map(table.columns, (c, i) => (
                    <TableHeaderColumn key={i} style={{ ...columnStyle, width: c.width || 100, ...(i === 0 && { paddingLeft: 8 }) }} className={(c.isRedDay || c.isWeekend) ? 'weekend-column' : ''}>
                      {c.title}
                    </TableHeaderColumn>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody
                displayRowCheckbox={false}
                deselectOnClickaway={false}
                showRowHover
                stripedRows={false}
              >
                {!loading ? map(currentData, (row, index) => (
                  <TableRow key={index} displayBorder>(
                    {map(table.columns, (c, i) => (
                      <TableRowColumn key={i} style={{ ...columnStyle, width: c.width || 100, ...(i === 0 && { paddingLeft: 8 }) }} className={(c.isRedDay || c.isWeekend) ? 'weekend-column' : ''}>
                        {c.render ? c.render(row[c.dataIndex]) : row[c.dataIndex]}
                        {row.ClockCardEntries[c.dataIndex] ? getCell(row.ClockCardEntries[c.dataIndex]) : null}
                      </TableRowColumn>
                    ))})
                  </TableRow>
                )) : null}
              </TableBody>
            </Table>
            <div style={placedDiv}>
              {map(inOutHoverTexts, (t, i) => (
                <div key={i}>
                  <div>{t.inOutType}: {t.inOutClock}</div>
                  <div>{t.comment}</div>
                  {i !== inOutHoverTexts.length - 1 && <Divider style={{ marginTop: 12, marginBottom: 12 }} />}
                </div>
              ))}
            </div>
          </div>
        )}
      </Pagination>
    </Paper>
  );
};

InOutTable.propTypes = {
  table: PropTypes.shape({
    columns: PropTypes.array.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  entries: PropTypes.array.isRequired,
};

export default connect()(InOutTable);



// WEBPACK FOOTER //
// ./src/sites/inOut/InOutTable.js